
import { StandardQuizQuestion } from '@/types/quizTypes';

export interface QuizStandardizationConfig {
  minimumQuestions: number;
  passThreshold: number;
  timeLimit: number;
  difficultyDistribution: {
    easy: number;
    medium: number;
    hard: number;
  };
  requiredExplanations: boolean;
  categoryDistribution: boolean;
}

export const DEFAULT_QUIZ_CONFIG: QuizStandardizationConfig = {
  minimumQuestions: 8,
  passThreshold: 75,
  timeLimit: 600, // 10 minutes
  difficultyDistribution: {
    easy: 30, // 30%
    medium: 50, // 50%
    hard: 20 // 20%
  },
  requiredExplanations: true,
  categoryDistribution: true
};

export const validateQuizQuality = (questions: StandardQuizQuestion[], config = DEFAULT_QUIZ_CONFIG) => {
  const issues: string[] = [];
  const warnings: string[] = [];

  // Check minimum questions
  if (questions.length < config.minimumQuestions) {
    issues.push(`Quiz has ${questions.length} questions but needs at least ${config.minimumQuestions}`);
  }

  // Check for explanations
  if (config.requiredExplanations) {
    const missingExplanations = questions.filter(q => !q.explanation);
    if (missingExplanations.length > 0) {
      issues.push(`${missingExplanations.length} questions missing explanations`);
    }
  }

  // Check difficulty distribution
  const difficultyCount = {
    easy: questions.filter(q => q.difficulty === 'easy').length,
    medium: questions.filter(q => q.difficulty === 'medium').length,
    hard: questions.filter(q => q.difficulty === 'hard').length
  };

  const totalQuestions = questions.length;
  const actualDistribution = {
    easy: (difficultyCount.easy / totalQuestions) * 100,
    medium: (difficultyCount.medium / totalQuestions) * 100,
    hard: (difficultyCount.hard / totalQuestions) * 100
  };

  // Check if distribution is too far off (allow 10% variance)
  Object.keys(config.difficultyDistribution).forEach(difficulty => {
    const expected = config.difficultyDistribution[difficulty as keyof typeof config.difficultyDistribution];
    const actual = actualDistribution[difficulty as keyof typeof actualDistribution];
    
    if (Math.abs(expected - actual) > 15) {
      warnings.push(`${difficulty} difficulty: expected ~${expected}%, got ${actual.toFixed(1)}%`);
    }
  });

  // Check question quality
  questions.forEach((question, index) => {
    if (!question.question || question.question.trim().length < 10) {
      issues.push(`Question ${index + 1}: Question text too short or missing`);
    }

    if (!question.options || question.options.length < 3) {
      issues.push(`Question ${index + 1}: Need at least 3 options`);
    }

    if (question.correctAnswer < 0 || question.correctAnswer >= question.options.length) {
      issues.push(`Question ${index + 1}: Invalid correct answer index`);
    }

    // Check for option quality
    if (question.options) {
      const shortOptions = question.options.filter(opt => opt.trim().length < 3);
      if (shortOptions.length > 0) {
        warnings.push(`Question ${index + 1}: Has very short answer options`);
      }
    }
  });

  return {
    isValid: issues.length === 0,
    score: Math.max(0, 100 - (issues.length * 20) - (warnings.length * 5)),
    issues,
    warnings,
    statistics: {
      totalQuestions: questions.length,
      difficultyDistribution: actualDistribution,
      withExplanations: questions.filter(q => q.explanation).length,
      categories: [...new Set(questions.map(q => q.category))].length
    }
  };
};

export const enhanceQuizQuality = (questions: StandardQuizQuestion[]): StandardQuizQuestion[] => {
  return questions.map(question => ({
    ...question,
    // Ensure all required fields are present
    id: question.id || `q-${Math.random().toString(36).substr(2, 9)}`,
    difficulty: question.difficulty || 'medium',
    category: question.category || 'general',
    points: question.points || (question.difficulty === 'easy' ? 10 : question.difficulty === 'hard' ? 25 : 15),
    type: question.type || 'multiple-choice',
    explanation: question.explanation || 'Explanation will be provided after quiz completion.',
    
    // Validate correctAnswer consistency
    correctAnswerIndex: question.correctAnswerIndex !== undefined ? question.correctAnswerIndex : question.correctAnswer
  }));
};

export const generateQuizReport = (questions: StandardQuizQuestion[]) => {
  const validation = validateQuizQuality(questions);
  
  return {
    ...validation,
    recommendations: generateRecommendations(validation),
    qualityGrade: getQualityGrade(validation.score)
  };
};

const generateRecommendations = (validation: any): string[] => {
  const recommendations: string[] = [];
  
  if (validation.statistics.totalQuestions < DEFAULT_QUIZ_CONFIG.minimumQuestions) {
    recommendations.push(`Add ${DEFAULT_QUIZ_CONFIG.minimumQuestions - validation.statistics.totalQuestions} more questions`);
  }
  
  if (validation.statistics.withExplanations < validation.statistics.totalQuestions) {
    recommendations.push('Add explanations to all questions');
  }
  
  if (validation.statistics.categories < 3) {
    recommendations.push('Diversify question categories for better coverage');
  }
  
  return recommendations;
};

const getQualityGrade = (score: number): string => {
  if (score >= 90) return 'A+ (Excellent)';
  if (score >= 80) return 'A (Very Good)';
  if (score >= 70) return 'B (Good)';
  if (score >= 60) return 'C (Acceptable)';
  return 'D (Needs Improvement)';
};
